<template>
  <Container>
    <div class="wizard__form">
      <header class="wizard__form__header">
        <h1 class="wizard__form__title" v-html="$t(`${namespace}.title`)" />
        <p class="wizard__form__subtitle" v-html="$t(`${namespace}.subtitle`)" />
      </header>

      <transition name="fade">
        <div v-if="employeeErrorField" class="wizard__form__error">
          <v-icon color="error">warning</v-icon> <span>{{ $t(`${namespace}.error`).replace('[LABEL]', employeeErrorField) }}</span>
        </div>
      </transition>

      <form
        class="form--custom"
        novalidate
        @submit.prevent="handleAddEmployee"
      >
        <v-layout align-center row wrap>
          <v-flex md10>
            <v-layout row wrap>
              <v-flex xs12 md4>
                <div class="mr-2--desktop">
                  <label>{{ $t(`${namespace}.fieldNameLabel`) }}*</label>
                  <v-text-field
                    v-model="employee.name"
                    :placeholder="$t(`${namespace}.fieldNamePlaceholder`)"
                    :error-messages="errors.collect($t(`${namespace}.fieldNameName`))"
                    v-validate="{ required: true }"
                    :data-vv-name="$t(`${namespace}.fieldNameName`)"
                    required
                    flat
                    outline
                    solo
                    color="accent"
                  />
                </div>
              </v-flex>

              <v-flex xs12 md4>
                <div class="mr-2--desktop">
                  <label>{{ $t(`${namespace}.fieldEmailLabel`) }}*</label>
                  <v-text-field
                    v-model="employee.email"
                    :placeholder="$t(`${namespace}.fieldEmailPlaceholder`)"
                    :error-messages="errors.collect($t(`${namespace}.fieldEmailName`))"
                    v-validate="{ required: true, email: true }"
                    :data-vv-name="$t(`${namespace}.fieldEmailName`)"
                    required
                    flat
                    outline
                    solo
                    color="accent"
                  />
                </div>
              </v-flex>

              <v-flex xs12 md4>
                <div class="mr-2--desktop">
                  <label>{{ $t(`${namespace}.fieldPhoneLabel`) }}*</label>
                  <v-text-field
                    v-model="phone"
                    :placeholder="$t(`${namespace}.fieldPhonePlaceholder`)"
                    :error-messages="errors.collect($t(`${namespace}.fieldPhoneName`))"
                    v-validate="{ required: true, regex: /^\(\d{2}\)\s(\d{5})-(\d{4})$/ }"
                    :data-vv-name="$t(`${namespace}.fieldPhoneName`)"
                    required
                    flat
                    outline
                    solo
                    color="accent"
                    maxlength="15"
                    @input="setPhoneMask($event, 'phone')"
                  />
                </div>
              </v-flex>
            </v-layout>
          </v-flex>

          <v-flex xs12 md2>
            <v-btn
              type="submit"
              color="accent"
              depressed
              round
              block
              @click="$ga.event('botao', 'clicou', 'adicionar_colaborador')"
            >
              {{ $t(`${namespace}.add`) }}
            </v-btn>
          </v-flex>
        </v-layout>
      </form>

      <div class="wizard__form__text">
        <div v-if="!isValidArray(employees)" class="text-xs-center pt-4">
          <p v-html="$t(`${namespace}.text`)" />
          <p v-html="$t(`${namespace}.addEmployessText`)" />
        </div>

        <div v-else>
          <v-tooltip
            v-for="(employee, index) in employees"
            :key="employee.email"
            slot="append"
            min-width="150"
            max-width="150"
            top
          >
            <v-chip
              ref="employee-chip"
              :data-id="`employee-${index}`"
              close
              slot="activator"
              @input="handleDeleteEmployee({ ...employee, index })"
            >
              {{ employee.name }}
            </v-chip>
            <span>{{ employee.name }}<br>{{ employee.phone | phoneWithDDD }}</span>
          </v-tooltip>
        </div>
      </div>

      <footer class="wizard__form__footer">
        <v-tooltip
          slot="append"
          min-width="200"
          max-width="200"
          top
          :disabled="Boolean(employees.length)"
        >
          <v-btn
            slot="activator"
            color="warning--gradient"
            class="mx-0"
            round
            depressed
            large
            @click="handleAddEmployees"
          >
            {{ $t(`${namespace}.addEmployees`) }}
          </v-btn>
          <span>Cadastre pelo menos um colaborador.</span>
        </v-tooltip>

        <div class="wizard__escape__container">
          <a
            :title="$t('Views.Wizard.welcome.skipTutorial')"
            class="wizard__escape"
            @click="openAccessPlatformDialog"
          >
            {{ $t('Views.Wizard.welcome.skipTutorial') }}
          </a>
        </div>
      </footer>
    </div>

    <ManyEmployessDialog
      :visible="manyEmployessDialog"
      @close="closeManyEmployessDialog"
    />
  </Container>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { isArray } from 'utils/functions'
import { masks } from 'utils/masks'
import Container from 'components/wizard/Container'
import ManyEmployessDialog from 'components/wizard/ManyEmployessDialog'

export default {
  name: 'Form',
  components: {
    Container,
    ManyEmployessDialog
  },
  computed: {
    ...mapState({
      globalCompany: (state) => state.company.selectedCompany
    })
  },
  data () {
    return {
      namespace: 'Views.Wizard.form',
      manyEmployessDialog: false,
      employeesLimit: 15,
      phone: null,
      employee: {
        name: null,
        email: null,
        phone: null
      },
      employees: [],
      employeeErrorField: null,
      employeeErrorIndex: null
    }
  },
  methods: {
    ...mapActions({
      showAlert: 'snackbar/show'
    }),

    isValidArray (val) {
      return isArray(val)
    },

    openManyEmployessDialog () {
      this.manyEmployessDialog = true
    },

    closeManyEmployessDialog () {
      this.manyEmployessDialog = false
    },

    setPhoneMask (val, mask) {
      this.phone = masks[mask](val)
      this.employee.phone = val.replace(/\D/g, '')
    },

    handleSetStep () {
      this.$emit('handleSetStep', {
        step: 'sms',
        employeesCount: this.employees.length,
        employeesList: this.employees.map(employee => employee.phone)
      })
    },

    openAccessPlatformDialog () {
      this.$emit('openAccessPlatformDialog')
    },

    resetEmployeeError () {
      this.employeeErrorField = null
      this.employeeErrorIndex = null
      this.errors.clear()
    },

    resetForm () {
      this.phone = null

      this.employee = {
        name: null,
        email: null,
        phone: null
      }

      this.resetEmployeeError()
    },

    handleDeleteEmployee (employee) {
      this.$ga.event('botao', 'clicou', 'fechar_colaborador')

      if (this.employeeErrorIndex === employee.index) {
        this.resetEmployeeError()
      }

      const employees = this.employees.filter(e => e.email !== employee.email)
      this.employees = employees
    },

    addClassError () {
      const elem = document.querySelector(`[data-id="employee-${this.employeeErrorIndex}"]`)
      elem.classList.add('error2')
    },

    removeClassError () {
      const elems = this.$refs['employee-chip']
      if (elems) {
        elems.forEach(item => item.$el.classList.remove('error2'))
      }
    },

    checkIfEmployeeExists (name, email, phone) {
      const { vmId } = this.errors

      return this.employees.find((e, i) => {
        if (e.email === email) {
          this.employeeErrorField = 'e-mail'
          this.employeeErrorIndex = i

          this.errors.add({
            id: '101',
            vmId,
            field: '"e-mail"',
            msg: null,
            rule: 'required',
            scope: null
          })

          return true
        }

        if (e.phone === phone) {
          this.employeeErrorField = 'telefone'
          this.employeeErrorIndex = i

          this.errors.add({
            id: '102',
            vmId,
            field: '"telefone"',
            msg: null,
            rule: 'required',
            scope: null
          })

          return true
        }

        return false
      })
    },

    async handleAddEmployee () {
      if (this.employees.length >= this.employeesLimit) {
        this.openManyEmployessDialog()
        return
      }

      try {
        const isValid = await this.$validator.validateAll()

        if (isValid) {
          const { name, email, phone } = this.employee

          this.removeClassError()

          if (this.checkIfEmployeeExists(name, email, phone)) {
            this.addClassError()
            return
          }

          const employee = {
            name,
            phone,
            phoneCountry: 'BRA',
            email,
            companyId: this.globalCompany.id
          }

          this.employees = [
            ...this.employees,
            employee
          ]

          this.resetForm()
        }
      } catch (err) {
        console.log('ERROR', err)
      }
    },

    async handleAddEmployees () {
      this.$ga.event('botao', 'clicou', 'cadastrar_colaborador')

      if (!this.employees.length) return

      try {
        await this.$apiService.addEmployeesBulk({
          employees: this.employees
        })
        this.handleSetStep()
      } catch (err) {
        console.log('ERROR', err)
        this.showAlert({
          type: 'error',
          message: this.$t(`${this.namespace}.errorMessage`)
        })
      }
    }
  },
  watch: {
    employees (val) {
      if (val && val.length) {
        this.resetEmployeeError()
      }
    }
  }
}
</script>

<style lang="scss">
.wizard__form {

  .v-messages__message {
    font-size: 10px;
  }
}
</style>

<style lang="scss" scoped>
.wizard__form {

  .form--custom {
    padding: 0 24px;

    @media (min-width: 600px) {
      padding: 0 40px;
    }
  }
}

.wizard__form__header {
  padding: 0 24px;
  text-align: center;
}

.mr-2--desktop {

  @media (min-width: 600px) {
    margin-right: 16px;
  }
}

.wizard__form__title,
.wizard__form__subtitle {
  font-family: 'Montserrat', sans-serif;
}

.wizard__form__title {
  font-size: 24px;
  font-weight: 800;
  line-height: normal;
  margin-bottom: 10px;

  @media (min-width: 992px) {
    font-size: 36px;
  }

  span {
    color: $accent;
  }
}

.wizard__form__subtitle {
  color: $gray-6;
  font-size: 16px;
  font-weight: 300;
  line-height: 20px;
  margin-bottom: 40px;

  strong {
    color: $gray-8 !important;
    font-weight: 600;
  }
}

.wizard__form__error {
  align-items: center;
  background-color: $error2;
  color: #a94442;
  display: flex;
  min-height: 60px;
  justify-content: center;
  line-height: normal;
  margin-bottom: 30px;
  text-align: center;

  span {
    margin-left: 5px;
  }
}

.wizard__form__text {
  color: $gray-6;
  font-weight: 300;
  padding: 10px 24px 15px;

  @media (min-width: 992px) {
    min-height: 175px;
    padding: 0 40px;
  }

  strong {
    color: $gray-8;
    font-weight: 600;
  }
}

.wizard__form__footer {
  border-top: 1px solid #eee;
  padding-top: 20px;
  margin: 0 24px;
  text-align: center;

  @media (min-width: 992px) {
    margin: 0 40px;
  }
}
</style>
